import React, { useEffect, useRef, useState } from 'react'
import {
  OverviewProjectsTableProps,
  overviewProjectsTableTotalWidths,
  overviewProjectsTableWidths,
  expandedOverviewProjectsTableWidths
} from './OverviewProjectsTable.types'
import styles from './OverviewProjectsTable.module.scss'
import cl from 'classnames'
import {
  Button,
  PaginationButtons,
  SelectTreeView,
  TableBodyContainer,
  TableColHeaderProps,
  TableContainer,
  TableHeader,
  TableHorizontalScrollControls,
  Text2
} from 'components/shared'
import { IconFilterEmpty, IconMinusCircle, IconPlusCircle } from 'assets'
import { ShowFilterButton } from './ShowFilterButton'
import { User } from 'utilities'
import { inject } from 'mobx-react'
import {
  STATISTICS_OVERVIEW_TABLE_STORE,
  STATISTICS_OVERVIEW_STORE,
  StatsOverviewTableSortProps
} from 'store'
import { observer } from 'mobx-react-lite'
import { getOverviewProjectsTableFlexWidth } from './OverviewProjectsTable.utils'
import { OverviewProjectsTableBodyLine } from './OverviewProjectsTableBodyLine'
import { LoadingSpinner } from 'components'
import { ViewerEventEnums } from 'models'
import { StatsTableColType } from '../../../../store/StatsBaseTableStore'
import { toJS } from "mobx"

const OverviewProjectsTableComponent = (props: OverviewProjectsTableProps) => {
  const {
    className,
    style,
    [STATISTICS_OVERVIEW_TABLE_STORE]: {
      isExpandedBlobsColumn,
      setIsExpandedBlobsColumn,
      isExpandedProductPagesColumn,
      setIsExpandedProductPagesColumn,
      isExpandedPostersColumn,
      setIsExpandedPostersColumn,
      searchResults,
      paginationInfo,
      onPageSelect,
      tableSettings,
      onColumnDragEnd,
      processing,
      setProcessing,
      selectorColLabel,
      legendEntityLabel,
      isViewerEventShown,
      onEventTypeShowCheckboxClick,
      isEventTypeShowCheckboxDisabled,
      getFilterPropsByColLabel,
      getEventLabel,
      getSortDirection,
      handleSortDirectionChange,
      isColumnMarked
    },
    [STATISTICS_OVERVIEW_STORE]: {
      isByExpensesTabSelected
    }
  } = props

  useEffect(() => {
    setProcessing(true)
  }, [setProcessing])

  const overviewProjectsTableFlexWidth =
    getOverviewProjectsTableFlexWidth(tableSettings.draggableColumnsCount)

  const expensesLabel = User.isShopOwner() ? 'Expenses' : 'Revenue'

  const getDraggableTableColProps = (): TableColHeaderProps[] => {
    const columns: TableColHeaderProps[] = [
      {
        draggable: true,
        label: tableSettings.mainEntityColLabel,
        widthString: overviewProjectsTableFlexWidth,
        filterComponent: (
          <SelectTreeView
            {...getFilterPropsByColLabel(StatsTableColType.MAIN)}
          />
        ),
        style: {
          minWidth: overviewProjectsTableWidths.draggableColMinWidth,
          maxWidth: 'unset'
        }
      },
      {
        draggable: true,
        label: tableSettings.primaryCorrespondingEntityColLabel,
        widthString: overviewProjectsTableFlexWidth,
        filterComponent: (
          <SelectTreeView
            {...getFilterPropsByColLabel(StatsTableColType.PRIMARY)}
          />
        ),
        style: {
          minWidth: overviewProjectsTableWidths.draggableColMinWidth,
          maxWidth: 'unset'
        }
      }
    ]

    if (tableSettings.draggableColumnsCount > 2) { //'Store' column
      columns.push({
        draggable: true,
        label: tableSettings.secondaryCorrespondingEntityColLabel,
        widthString: overviewProjectsTableFlexWidth,
        filterComponent: (
          <SelectTreeView
            {...getFilterPropsByColLabel(StatsTableColType.SECONDARY)}
          />
        ),
        style: {
          minWidth: overviewProjectsTableWidths.draggableColMinWidth,
          maxWidth: 'unset'
        }
      })
    }

    return columns
  }

  const widthCorrection = isByExpensesTabSelected ? 0 : 21

  const tableCols: TableColHeaderProps[] = [{
    label: selectorColLabel,
    widthPx: overviewProjectsTableWidths.selector,
    filterComponent: (
      <IconFilterEmpty
        style={{
          fill: styles.secondaryContent,
          marginLeft: -5
        }}
      />
    ),
  },
  ...getDraggableTableColProps(),
  {
    label: getEventLabel(ViewerEventEnums.BLOB_TRIGGERED),
    preset: 'grouped',
    widthPx: isByExpensesTabSelected 
    ? (isExpandedBlobsColumn 
    ? expandedOverviewProjectsTableWidths.blobs 
    : overviewProjectsTableWidths.blobs - widthCorrection)
    : overviewProjectsTableWidths.blobs - widthCorrection,
    groupLabel: isByExpensesTabSelected ? expensesLabel : 'Interactions',
    groupWidth: overviewProjectsTableTotalWidths.groupedCols,
    filterComponent: (
      <>
      {isByExpensesTabSelected && (!isExpandedBlobsColumn 
      ? <IconPlusCircle onClick={() => setIsExpandedBlobsColumn(!isExpandedBlobsColumn)}/>
      : <IconMinusCircle onClick={() => setIsExpandedBlobsColumn(!isExpandedBlobsColumn)} />)}
      <ShowFilterButton
        isChecked={isViewerEventShown(ViewerEventEnums.BLOB_TRIGGERED)}
        onClick={() => onEventTypeShowCheckboxClick(ViewerEventEnums.BLOB_TRIGGERED)}
        disabled={isEventTypeShowCheckboxDisabled(ViewerEventEnums.BLOB_TRIGGERED)}
      />
      </>
    ),
    isColumnMarked: isColumnMarked(StatsOverviewTableSortProps.BLOBS),
    sortDirection: getSortDirection(StatsOverviewTableSortProps.BLOBS),
    setSortDirection: handleSortDirectionChange(StatsOverviewTableSortProps.BLOBS),
    bigLabel: !isByExpensesTabSelected
  },
  {
    label: getEventLabel(ViewerEventEnums.PRODUCT_PAGE_OPENED), //here TODO
    preset: 'grouped',
    widthPx: isByExpensesTabSelected 
    ? (isExpandedProductPagesColumn 
    ? expandedOverviewProjectsTableWidths.productPages 
    : overviewProjectsTableWidths.productPages - widthCorrection)
    : overviewProjectsTableWidths.productPages - widthCorrection,
    filterComponent: (
      <>
      {isByExpensesTabSelected && (!isExpandedProductPagesColumn 
      ? <IconPlusCircle onClick={() => setIsExpandedProductPagesColumn(!isExpandedProductPagesColumn)}/>
      : <IconMinusCircle onClick={() => setIsExpandedProductPagesColumn(!isExpandedProductPagesColumn)} />)}
      <ShowFilterButton
        isChecked={isViewerEventShown(ViewerEventEnums.PRODUCT_PAGE_OPENED)}
        onClick={() =>
          onEventTypeShowCheckboxClick(ViewerEventEnums.PRODUCT_PAGE_OPENED)}
        disabled={
          isEventTypeShowCheckboxDisabled(ViewerEventEnums.PRODUCT_PAGE_OPENED)
        }
      />
      </>
    ),
    isColumnMarked: isColumnMarked(StatsOverviewTableSortProps.PRODUCT_PAGES),
    sortDirection: getSortDirection(StatsOverviewTableSortProps.PRODUCT_PAGES),
    setSortDirection: handleSortDirectionChange(StatsOverviewTableSortProps.PRODUCT_PAGES),
    bigLabel: !isByExpensesTabSelected
  },
  {
    label: getEventLabel(ViewerEventEnums.POSTER_CLICKED),
    preset: 'grouped',
    widthPx: isByExpensesTabSelected 
    ? (isExpandedPostersColumn 
    ? expandedOverviewProjectsTableWidths.posters 
    : overviewProjectsTableWidths.posters - widthCorrection)
    : overviewProjectsTableWidths.posters - widthCorrection,
    filterComponent: (
      <>
      {isByExpensesTabSelected && (!isExpandedPostersColumn 
      ? <IconPlusCircle onClick={() => setIsExpandedPostersColumn(!isExpandedPostersColumn)}/>
      : <IconMinusCircle onClick={() => setIsExpandedPostersColumn(!isExpandedPostersColumn)} />)}
      <ShowFilterButton
        isChecked={isViewerEventShown(ViewerEventEnums.POSTER_CLICKED)}
        onClick={() => onEventTypeShowCheckboxClick(ViewerEventEnums.POSTER_CLICKED)}
        disabled={isEventTypeShowCheckboxDisabled(ViewerEventEnums.POSTER_CLICKED)}
      />
      </>
    ),
    isColumnMarked: isColumnMarked(StatsOverviewTableSortProps.POSTERS),
    sortDirection: getSortDirection(StatsOverviewTableSortProps.POSTERS),
    setSortDirection: handleSortDirectionChange(StatsOverviewTableSortProps.POSTERS),
    bigLabel: !isByExpensesTabSelected
  },
  {
    label: getEventLabel(ViewerEventEnums.PURCHASE),
    preset: 'grouped',
    widthPx: overviewProjectsTableWidths.purchases - widthCorrection,
    filterComponent: (
      <ShowFilterButton
        isChecked={isViewerEventShown(ViewerEventEnums.PURCHASE)}
        onClick={() => onEventTypeShowCheckboxClick(ViewerEventEnums.PURCHASE)}
        disabled={isEventTypeShowCheckboxDisabled(ViewerEventEnums.PURCHASE)}
      />
    ),
    isColumnMarked: isColumnMarked(StatsOverviewTableSortProps.PURCHASES),
    sortDirection: getSortDirection(StatsOverviewTableSortProps.PURCHASES),
    setSortDirection: handleSortDirectionChange(StatsOverviewTableSortProps.PURCHASES)
  },
  {
    label: getEventLabel(ViewerEventEnums.VOV_CLICKED),
    preset: 'grouped',
    widthPx: overviewProjectsTableWidths.vov - widthCorrection,
    filterComponent: (
      <ShowFilterButton
        isChecked={isViewerEventShown(ViewerEventEnums.VOV_CLICKED)}
        onClick={() => onEventTypeShowCheckboxClick(ViewerEventEnums.VOV_CLICKED)}
        disabled={isEventTypeShowCheckboxDisabled(ViewerEventEnums.VOV_CLICKED)}
      />
    ),
    isColumnMarked: isColumnMarked(StatsOverviewTableSortProps.VOV),
    sortDirection: getSortDirection(StatsOverviewTableSortProps.VOV),
    setSortDirection: handleSortDirectionChange(StatsOverviewTableSortProps.VOV),
    bigLabel: !isByExpensesTabSelected
  },
  {
    label: 'Total',
    preset: 'grouped',
    widthPx: overviewProjectsTableWidths.total - widthCorrection,
    isColumnMarked: isColumnMarked(StatsOverviewTableSortProps.TOTAL),
    sortDirection: getSortDirection(StatsOverviewTableSortProps.TOTAL),
    setSortDirection: handleSortDirectionChange(StatsOverviewTableSortProps.TOTAL)
  }]

  if (!isByExpensesTabSelected) {
    tableCols.splice(7, 0, {
      label: 'Poster up time',
      preset: 'grouped',
      widthPx: overviewProjectsTableWidths.posters - widthCorrection,
      filterComponent: (
        <ShowFilterButton
          isChecked={isViewerEventShown(ViewerEventEnums.POSTER_ATTACHED)}
          onClick={() => onEventTypeShowCheckboxClick(ViewerEventEnums.POSTER_ATTACHED)}
          disabled={isEventTypeShowCheckboxDisabled(ViewerEventEnums.POSTER_ATTACHED)}
        />
      ),
      isColumnMarked: isColumnMarked(StatsOverviewTableSortProps.POSTERS),
      // sortDirection: getSortDirection(StatsOverviewTableSortProps.POSTERS),
      setSortDirection: handleSortDirectionChange(StatsOverviewTableSortProps.POSTERS),
      bigLabel: !isByExpensesTabSelected
    },{
      label: getEventLabel(ViewerEventEnums.ADD_TO_CART),
      preset: 'grouped',
      widthPx: overviewProjectsTableWidths.purchases - widthCorrection - 3,
      filterComponent: (
        <ShowFilterButton
          isChecked={isViewerEventShown(ViewerEventEnums.ADD_TO_CART)}
          onClick={() => onEventTypeShowCheckboxClick(ViewerEventEnums.ADD_TO_CART)}
          disabled={isEventTypeShowCheckboxDisabled(ViewerEventEnums.ADD_TO_CART)}
        />
      ),
      isColumnMarked: isColumnMarked(StatsOverviewTableSortProps.ADDED_TO_CART),
      sortDirection: getSortDirection(StatsOverviewTableSortProps.ADDED_TO_CART),
      setSortDirection: handleSortDirectionChange(StatsOverviewTableSortProps.ADDED_TO_CART),
      bigLabel: !isByExpensesTabSelected
    })
  }

  const tableBodyContainerRef = useRef<HTMLDivElement>(null)

  const [tableHeaderMarginLeft, setTableHeaderMarginLeft] = useState<number>(0)
  const [tableBodyScrollWidth, setTableBodyScrollWidth] = useState<number>(undefined)

  return (
    <div
      className={cl(styles.container, className)}
      style={style}
    >
      <Text2 style={{ color: styles.secondaryContent }}>
        *Select up to six {legendEntityLabel} to compare them in a graph.
        Hide columns to temporarily remove them from the graph.
        Drag a column in first position to filter by it.
      </Text2>

      <TableContainer className={styles.tableContainer}>
        <TableHeader
          tableColumns={tableCols}
          isByExpensesTabSelected={isByExpensesTabSelected}
          isExpandedBlobsColumn={isExpandedBlobsColumn}
          isExpandedProductPagesColumn={isExpandedProductPagesColumn}
          isExpandedPostersColumn={isExpandedPostersColumn}
          dragDropContextProps={{
            onDragEnd: onColumnDragEnd
          }}
          style={{
            marginLeft: tableHeaderMarginLeft,
            width: tableBodyScrollWidth,
            zIndex: 1
          }}
        />

        <TableHorizontalScrollControls
          containerRef={tableBodyContainerRef}
          onScrollLeft={(pixels) => setTableHeaderMarginLeft(-pixels)}
          onScrollWidthChange={(scrollWidth) => setTableBodyScrollWidth(scrollWidth)}
        />

        <TableBodyContainer
          ref={tableBodyContainerRef}
          className={styles.tableBodyContainer}
        >
          {processing
            ? (
              <LoadingSpinner
                show
                noBackground
                color={styles.primary}
                containerStyleProps={{
                  className: styles.loadingSpinner
                }}
              />
            )
            : searchResults.map(tableEntity => (
              <OverviewProjectsTableBodyLine
                key={tableEntity.mainEntity.id}
                tableEntity={tableEntity}
                style={{
                  width: tableBodyScrollWidth
                }}
              />
            ))}
        </TableBodyContainer>
        <PaginationButtons
          alwaysShow
          onPageSelect={onPageSelect}
          {...paginationInfo}
          style={{
            padding: '18px 0 22px'
          }}
        />
      </TableContainer>

    </div>
  )
}

const OverviewProjectsTable = inject(
  STATISTICS_OVERVIEW_TABLE_STORE, STATISTICS_OVERVIEW_STORE
)(observer(OverviewProjectsTableComponent))
export { OverviewProjectsTable }
